<script setup>
// Profile cards — a heading, optional intro line, and a responsive grid of
// ProfileCard (photo, name, pronouns, title, LinkedIn, bio with a "Show more"
// expander). Used for the /about/our-people team grid and the Academy's
// "Graduate stories". The grid mirrors the /our-work grid so the cards match
// those in width, gutter and (equalised) height. Renders from a `profileCards`
// block.
import ProfileCard from '@/components/ProfileCard.vue'

import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  block: { type: Object, required: true },   // { heading?, intro?, photoRatio?: 'square', items: [{ name, pronouns?, title?, photo?, photoPosition?, linkedin?, quote?, bio }] }
})

// Photos use a 5/3 landscape crop by default (matching the /our-work cards); a
// `photoRatio: 'square'` block opts into uncropped 1:1 headshots.
const photoAspect = computed(() => (props.block.photoRatio === 'square' ? '1 / 1' : '5 / 3'))

// Equal-height collapsed cards — client-side only.
//
// The grid keeps `align-items: start` (see <style>) so that expanding one
// card's bio never stretches its row-mates. That deliberately leaves collapsed
// cards ragged when their lead-quotes differ in length. To line the collapsed
// cards up again WITHOUT reintroducing that bug, we measure the tallest
// collapsed card and set it as a `min-height` on every card: a card that's
// expanded simply grows past the baseline on its own, while its siblings hold
// at the shared min-height. ProfileCard's flex body then pins "Show more" to
// the bottom, so the slack reads as intentional footer spacing.
//
// Only runs where cards carry quotes (the Academy "Graduate stories") — the
// /about/our-people team grid has no quotes, so it is left exactly as it was.
// Like ProfileCard's own "Show more" measurement, this is browser-only: the
// static build ships the cards un-equalised and the client lines them up on
// hydration and on resize (column widths, and therefore heights, change).
const gridEl = ref(null)
const equalise = computed(() => props.block.items.some(i => i.quote))
let ro = null

function equaliseHeights () {
  const grid = gridEl.value
  if (!grid) return
  ro?.disconnect()   // ignore the resize our own writes trigger

  const cards = [...grid.querySelectorAll('.profile-card')]
  cards.forEach(c => { c.style.minHeight = '' })   // clear, so we read natural height

  // Baseline is the tallest *collapsed* card (bio still clamped); skip any the
  // user has expanded so their extra height can't inflate the shared minimum.
  const collapsed = cards.filter(c => c.querySelector('.bio.clamped'))
  if (collapsed.length) {
    const tallest = Math.max(...collapsed.map(c => c.offsetHeight))
    cards.forEach(c => { c.style.minHeight = `${tallest}px` })
  }

  if (ro) requestAnimationFrame(() => grid && ro.observe(grid))
}

onMounted(() => {
  if (!equalise.value || typeof ResizeObserver === 'undefined') return
  ro = new ResizeObserver(() => equaliseHeights())
  // Wait for ProfileCard's own onMounted clamp/toggle measurement to settle.
  requestAnimationFrame(() => requestAnimationFrame(equaliseHeights))
  // Re-measure once web fonts load, in case metrics shift the line count.
  if (document.fonts?.ready) document.fonts.ready.then(equaliseHeights)
})

onBeforeUnmount(() => ro?.disconnect())
</script>

<template>
  <div class="profile-cards">
    <h2 v-if="block.heading" class="block-heading">{{ block.heading }}</h2>
    <p v-if="block.intro" class="intro">{{ block.intro }}</p>

    <div ref="gridEl" class="grid">
      <ProfileCard
        v-for="person in block.items"
        :key="person.name"
        :name="person.name"
        :pronouns="person.pronouns"
        :title="person.title"
        :photo="person.photo"
        :photo-position="person.photoPosition"
        :linkedin="person.linkedin"
        :quote="person.quote"
        :bio="person.bio"
        :photo-aspect="photoAspect"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-cards {
  .block-heading {
    margin: 0 0 $space-4;
    font-size: $font-size-h2;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
    font-weight: 500;
    color: var(--color-ink);
  }

  .intro {
    margin: 0 0 $space-6;
    font-size: $font-size-lg;
    line-height: $lh-base;
    color: var(--color-text);
  }

  // Mirrors the /our-work grid (OurWork.vue) in width and gutter, but each
  // card keeps its own natural height rather than equalising to its row.
  // Two deliberate departures from OurWork's `1fr` + stretch recipe:
  // `grid-auto-rows: auto` sizes each row independently (with `1fr` on a
  // height-auto container, the browser equalises EVERY row in the grid to
  // the tallest row's content, not just the row being sized); `align-items:
  // start` stops cards from stretching to match a taller row-mate. Both
  // matter because ProfileCard's bio expands in place ("Show more") — with
  // the OurWork recipe, expanding one card grew its whole row (`auto` fixes
  // that) and every card sharing that row visibly stretched to match it
  // (`start` fixes that): only the clicked card grows, siblings stay put.
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    align-items: start;
    gap: $space-5;

    @include from-md { grid-template-columns: repeat(2, 1fr); }
    @include from-lg { grid-template-columns: repeat(3, 1fr); }
  }
}
</style>
